/**
 * Queries and mutations for 👍/👎 feedback.
 *
 * Its own key root: a verdict changes nothing the assistant or ticket caches
 * hold, and the admin review is a separate resource from the transcript.
 *
 * `verdict(messageId)` is not a query anyone runs — it is a cache slot, the
 * same trick `assistantKeys.serviceRequest` uses. The controls under a reply
 * unmount and remount when a finished turn hands over from the live stream to
 * the persisted transcript, and a rating given in that window must survive it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AdminErrorListResponse,
  type AdminFeedbackListResponse,
  type AdminFeedbackSummaryResponse,
  adminGetFeedbackSummary as sdkAdminGetFeedbackSummary,
  adminListErrorFeedback as sdkAdminListErrorFeedback,
  adminListFeedback as sdkAdminListFeedback,
  adminListLeadershipFeedback as sdkAdminListLeadershipFeedback,
  adminListRecentFixes as sdkAdminListRecentFixes,
  adminReopenFeedback as sdkAdminReopenFeedback,
  adminResolveFeedback as sdkAdminResolveFeedback,
  type FeedbackEntry,
  type FeedbackItemKind,
  type FeedbackRating,
  type LeadershipFeedbackListResponse,
  type MyFeedback,
  type RecentFixesResponse,
  type Resolution,
  submitFeedback as sdkSubmitFeedback,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import type { FeedbackRequestBody } from "./feedback-draft";
import type { StatusFilter } from "./resolution-helpers";

export const feedbackKeys = {
  all: ["feedback"] as const,
  verdict: (messageId: string) => [...feedbackKeys.all, "verdict", messageId] as const,
  admin: () => [...feedbackKeys.all, "admin"] as const,
  adminList: (params: AdminFeedbackParams) => [...feedbackKeys.admin(), "list", params] as const,
  adminSummary: () => [...feedbackKeys.admin(), "summary"] as const,
  adminErrors: (params: { ref: string | null; status: StatusFilter }) =>
    [...feedbackKeys.admin(), "errors", params] as const,
  adminLeadership: (params: { status: StatusFilter }) =>
    [...feedbackKeys.admin(), "leadership", params] as const,
  recentFixes: () => [...feedbackKeys.admin(), "recent-fixes"] as const,
};

/** Admin data may be a minute stale — it is a review screen, not a live feed. */
const ADMIN_STALE_MS = 60_000;

/**
 * Record the caller's verdict on one reply.
 *
 * `retry: false`: a failure is shown under the reply with its own retry
 * button, and a silent automatic replay would only hide that it happened.
 */
export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: FeedbackRequestBody): Promise<FeedbackEntry> => {
      const { data } = await sdkSubmitFeedback({ body, throwOnError: true });
      return data;
    },
    retry: false,
    onSuccess: (entry) => {
      logger.debug("[feedback] recorded", entry.target_id, entry.rating);
      queryClient.setQueryData<MyFeedback>(feedbackKeys.verdict(entry.target_id), {
        rating: entry.rating,
        reason_codes: entry.reason_codes ?? [],
        comment: entry.comment ?? null,
      });
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.admin() });
    },
  });
}

export interface AdminFeedbackParams {
  rating: FeedbackRating | null;
  /** Open / Resolved / All (9 Oct). */
  status: StatusFilter;
  limit: number;
  offset: number;
}

/** One page of the latest feedback, newest change first. Admin only server-side. */
export function useAdminFeedback(params: AdminFeedbackParams) {
  return useQuery({
    queryKey: feedbackKeys.adminList(params),
    queryFn: async (): Promise<AdminFeedbackListResponse> => {
      const { data } = await sdkAdminListFeedback({
        query: {
          limit: params.limit,
          offset: params.offset,
          status: params.status,
          ...(params.rating === null ? {} : { rating: params.rating }),
        },
        throwOnError: true,
      });
      return data;
    },
    staleTime: ADMIN_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/** 👍 vs 👎 over the last 7 and 30 days. */
export function useAdminFeedbackSummary() {
  return useQuery({
    queryKey: feedbackKeys.adminSummary(),
    queryFn: async (): Promise<AdminFeedbackSummaryResponse> => {
      const { data } = await sdkAdminGetFeedbackSummary({ throwOnError: true });
      return data;
    },
    staleTime: ADMIN_STALE_MS,
  });
}

/**
 * The Errors (auto-logged) view: one row per error and endpoint, last 7 days.
 *
 * `ref` narrows it to the error a person quoted ("Ref. 7F3A12BC"), which is
 * how the Teams card links here.
 */
export function useAdminErrorFeedback(params: { ref: string | null; status: StatusFilter }) {
  return useQuery({
    queryKey: feedbackKeys.adminErrors(params),
    queryFn: async (): Promise<AdminErrorListResponse> => {
      const { data } = await sdkAdminListErrorFeedback({
        query:
          params.ref === null
            ? { status: params.status }
            : { ref: params.ref, status: params.status },
        throwOnError: true,
      });
      return data;
    },
    staleTime: ADMIN_STALE_MS,
  });
}

/** The "Leadership report" view: every comment sent from the report, newest first. */
export function useAdminLeadershipFeedback(params: { status: StatusFilter }) {
  return useQuery({
    queryKey: feedbackKeys.adminLeadership(params),
    queryFn: async (): Promise<LeadershipFeedbackListResponse> => {
      const { data } = await sdkAdminListLeadershipFeedback({
        query: { status: params.status },
        throwOnError: true,
      });
      return data;
    },
    staleTime: ADMIN_STALE_MS,
  });
}

/** One inbox item: which view it is in, and its key there. */
export interface ResolutionTarget {
  kind: FeedbackItemKind;
  key: string;
}

/**
 * Resolve an inbox item with a fix note (9 Oct). Refreshes every inbox view:
 * the item moves from Open to Resolved and the counts change.
 */
export function useResolveFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ResolutionTarget & { fixNote: string }): Promise<Resolution> => {
      const { data } = await sdkAdminResolveFeedback({
        body: { kind: vars.kind, key: vars.key, fix_note: vars.fixNote },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (_resolution, vars) => {
      logger.debug("[feedback] resolved", vars.kind, vars.key);
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.admin() });
    },
  });
}

/** Reopen a resolved inbox item; its fix note stays in the history. */
export function useReopenFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ResolutionTarget): Promise<Resolution> => {
      const { data } = await sdkAdminReopenFeedback({
        body: { kind: vars.kind, key: vars.key },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (_resolution, vars) => {
      logger.debug("[feedback] reopened", vars.kind, vars.key);
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.admin() });
    },
  });
}

/** The latest fix notes and the PR numbers in them, for the dialog's quick picks. */
export function useRecentFixes(enabled: boolean) {
  return useQuery({
    queryKey: feedbackKeys.recentFixes(),
    queryFn: async (): Promise<RecentFixesResponse> => {
      const { data } = await sdkAdminListRecentFixes({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: ADMIN_STALE_MS,
  });
}
